/**
 * QC charts — Fielding & DRS tab.
 *   Fielding impact · Catch & drop map · Reviews tracker
 */
import { useMemo } from "react";
import { FC, inOrder, niceWicket, placeShots, positionXY } from "./qcData";
import { Card, Empty, Field, Tile } from "./qcUi";
import { useTip } from "./useTip";

const isCatch = (a) => /^caught$/i.test(a);
const isDrop = (a) => /^dropped$/i.test(a);
const isRunOut = (a) => /^run out/i.test(a) && /,\s*out$/i.test(a);
const isRunOutAssist = (a) => /^run out- assist$/i.test(a);
const isMissedChance = (a) => /missed|not-out/i.test(a) || /^stumping missed$/i.test(a);
const isStumping = (a) => /^stumped$/i.test(a);

// ── 16. Fielding impact ──────────────────────────────────────────────
function FieldingImpact({ balls }) {
  const tip = useTip();
  const rows = useMemo(() => {
    const m = new Map();
    const get = (name, team) => {
      const r = m.get(name) || { name, team, saved: 0, given: 0, catches: 0, drops: 0, runOuts: 0, assists: 0, stumpings: 0, missed: 0, touches: 0 };
      m.set(name, r);
      return r;
    };
    for (const b of balls) {
      for (const f of b.fieldings) {
        if (!f.player) continue;
        const r = get(f.player, b.bowlingTeamName);
        r.touches += 1;
        r.saved += f.saved;
        r.given += f.misfielded + f.overthrow;
        if (isCatch(f.action)) r.catches += 1;
        else if (isDrop(f.action)) r.drops += 1;
        else if (isRunOut(f.action)) r.runOuts += 1;
        else if (isRunOutAssist(f.action)) r.assists += 1;
        else if (isStumping(f.action)) r.stumpings += 1;
        else if (isMissedChance(f.action)) r.missed += 1;
      }
    }
    return [...m.values()].sort((a, b) => (b.saved - b.given) - (a.saved - a.given) || b.touches - a.touches);
  }, [balls]);
  if (!rows.length) return <Card wide title="Fielding impact"><Empty>No fielding actions coded in this selection.</Empty></Card>;
  const maxAbs = Math.max(1, ...rows.map((r) => Math.abs(r.saved - r.given)));
  const tot = rows.reduce((t, r) => ({ saved: t.saved + r.saved, given: t.given + r.given, catches: t.catches + r.catches, drops: t.drops + r.drops }), { saved: 0, given: 0, catches: 0, drops: 0 });
  return (
    <Card wide title="Fielding impact" subtitle="Net runs = runs saved − (misfields + overthrows) · blue saved runs, orange gave them away">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
        <Tile label="Runs saved" value={tot.saved} />
        <Tile label="Runs given away" value={tot.given} sub="misfields + overthrows" />
        <Tile label="Catches" value={tot.catches} />
        <Tile label="Dropped" value={tot.drops} tone={tot.drops ? "warn" : undefined} />
      </div>
      <div data-tip-root className="relative overflow-x-auto">
        <table className="w-full text-[11px] min-w-[620px]">
          <thead className="text-text-secondary uppercase tracking-wider text-[9px]">
            <tr>
              <th className="text-left py-1 font-normal">Fielder</th>
              <th className="font-normal w-[30%]">Net runs</th>
              <th className="text-right font-normal">Saved</th><th className="text-right font-normal">Given</th>
              <th className="text-right font-normal">Ct</th><th className="text-right font-normal">Drop</th>
              <th className="text-right font-normal">RO</th><th className="text-right font-normal">St</th>
              <th className="text-right font-normal">Missed</th><th className="text-right font-normal">Touches</th>
            </tr>
          </thead>
          <tbody className="tabular-nums">
            {rows.map((r) => {
              const net = r.saved - r.given;
              const w = `${(Math.abs(net) / maxAbs) * 50}%`;
              return (
                <tr key={r.name} className="border-t border-border-default">
                  <td className="py-1.5 pr-2 text-text-primary whitespace-nowrap">{r.name}</td>
                  <td className="px-2">
                    <div className="relative h-3" onMouseMove={(ev) => tip.show(ev, [r.name, `Net ${net > 0 ? "+" : ""}${net} runs`, `${r.saved} saved · ${r.given} given away`])} onMouseLeave={tip.hide}>
                      <div className="absolute left-1/2 top-0 bottom-0 w-px" style={{ background: "var(--qc-axis)" }} />
                      {net !== 0 && (
                        <div className="absolute top-0.5 bottom-0.5" style={net > 0
                          ? { left: "50%", width: w, background: "var(--qc-s1)", borderRadius: "0 3px 3px 0" }
                          : { right: "50%", width: w, background: "var(--qc-s2)", borderRadius: "3px 0 0 3px" }} />
                      )}
                    </div>
                  </td>
                  <td className="text-right text-text-primary">{r.saved || ""}</td>
                  <td className="text-right text-text-primary">{r.given || ""}</td>
                  <td className="text-right text-text-secondary">{r.catches || ""}</td>
                  <td className={`text-right ${r.drops ? "text-amber-400 font-semibold" : "text-text-secondary"}`}>{r.drops || ""}</td>
                  <td className="text-right text-text-secondary">{r.runOuts + r.assists || ""}</td>
                  <td className="text-right text-text-secondary">{r.stumpings || ""}</td>
                  <td className="text-right text-text-secondary">{r.missed || ""}</td>
                  <td className="text-right text-text-secondary">{r.touches}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {tip.node}
      </div>
    </Card>
  );
}

// ── 17. Catch & drop map ─────────────────────────────────────────────
function CatchMap({ balls }) {
  const tip = useTip();
  const { events, unplaced } = useMemo(() => {
    const pins = new Map(placeShots(balls).map((p) => [p.b.i, p]));
    const out = [];
    let unplaced = 0;
    for (const b of balls) {
      const acts = b.fieldings.filter((f) => isCatch(f.action) || isDrop(f.action));
      // A caught dismissal with no fielding row still counts as a catch.
      if (!acts.length && /^CAUGHT/.test(b.wicketRaw || "")) acts.push({ action: "Caught", player: b.caughtBy, position: "", difficulty: "" });
      for (const f of acts) {
        const pin = pins.get(b.i);
        let xy = pin ? [pin.x, pin.y] : null;
        if (!xy) {
          const pos = positionXY(f.position || b.structuredPos);
          if (pos) xy = [pos[0], pos[1]];
        }
        if (!xy) { unplaced += 1; continue; }
        out.push({ b, f, x: xy[0], y: xy[1], drop: isDrop(f.action) });
      }
    }
    return { events: out, unplaced };
  }, [balls]);
  const catches = events.filter((e) => !e.drop);
  const drops = events.filter((e) => e.drop);
  if (!events.length) return <Card title="Catch & drop map"><Empty>{unplaced ? `${unplaced} catches or drops, but none has a pin or fielding position to place them.` : "No catches or drops in this selection."}</Empty></Card>;
  return (
    <Card title="Catch & drop map" subtitle={`${catches.length} caught · ${drops.length} dropped · placed by wagon pin, else the coded fielding position · right-hander's view`}>
      <div data-tip-root className="relative max-w-[380px] mx-auto">
        <svg viewBox="0 0 100 100" className="w-full h-auto block" role="img" aria-label="Catches and drops">
          <Field />
          {events.map((e, k) => (
            <g key={k} onMouseMove={(ev) => tip.show(ev, [
              `${e.drop ? "Dropped" : "Caught"} · ${e.b.label}`,
              `${e.f.player || "fielder not coded"}${e.f.position ? ` at ${e.f.position}` : ""}${e.f.difficulty ? ` · ${e.f.difficulty}` : ""}`,
              `${e.b.striker} off ${e.b.bowler}`,
            ])} onMouseLeave={tip.hide}>
              <circle cx={e.x} cy={e.y} r="3.2" fill="transparent" />
              {e.drop
                ? <circle cx={e.x} cy={e.y} r="1.7" fill="rgba(15,23,42,0.6)" stroke="#f97316" strokeWidth="0.7" />
                : <circle cx={e.x} cy={e.y} r="1.7" fill="#fff" stroke="rgba(15,23,42,0.8)" strokeWidth="0.4" />}
            </g>
          ))}
          <text x={FC} y="99" textAnchor="middle" fontSize="2.6" className="fill-text-secondary">bowler's end</text>
        </svg>
        {tip.node}
      </div>
      <div className="mt-2 flex justify-center gap-4 text-[10px] text-text-secondary">
        <span className="inline-flex items-center gap-1.5"><span className="inline-block w-2.5 h-2.5 rounded-full bg-white ring-1 ring-slate-900/60" />Caught</span>
        <span className="inline-flex items-center gap-1.5"><span className="inline-block w-2.5 h-2.5 rounded-full ring-2 ring-orange-500" />Dropped</span>
        {unplaced > 0 && <span>{unplaced} not placed (no pin or position)</span>}
      </div>
    </Card>
  );
}

// ── 18. Reviews (DRS) ────────────────────────────────────────────────
const DECISION = { WICKET: "Out", NOT_OUT: "Not out", WIDE: "Wide", NO_BALL: "No ball", LEGAL: "Legal" };

function Reviews({ balls }) {
  const { reviews, appeals, teams } = useMemo(() => {
    const reviews = [];
    let appeals = 0;
    for (const b of inOrder(balls)) {
      const d = b.drs;
      if (!d) continue;
      if (d.is_appealed) appeals += 1;
      if (d.review_team || d.review_team_side) reviews.push({ b, d, team: d.review_team?.short_name || d.review_team?.name || (d.review_team_side === "BAT" ? b.teamName : d.review_team_side === "BOWL" ? b.bowlingTeamName : "?") });
    }
    const teams = new Map();
    for (const r of reviews) {
      const t = teams.get(r.team) || { team: r.team, used: 0, won: 0 };
      t.used += 1;
      if (r.d.overruled) t.won += 1;
      teams.set(r.team, t);
    }
    return { reviews, appeals, teams: [...teams.values()] };
  }, [balls]);
  if (!reviews.length && !appeals) return <Card title="Reviews (DRS)"><Empty>No appeals or reviews coded in this selection.</Empty></Card>;
  return (
    <Card title="Reviews (DRS)" subtitle="Reviews taken per side and how they went · successful = the on-field call was overturned">
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-3">
        <Tile label="Appeals coded" value={appeals} />
        {teams.map((t) => (
          <Tile key={t.team} label={`${t.team} reviews`} value={`${t.used}`} sub={`${t.won} successful · ${t.used - t.won} lost`} tone={t.used - t.won >= 2 ? "warn" : undefined} />
        ))}
      </div>
      {reviews.length > 0 && (
        <table className="w-full text-[11px]">
          <thead className="text-text-secondary uppercase tracking-wider text-[9px]">
            <tr><th className="text-left py-1 font-normal">Ball</th><th className="text-left font-normal">Side</th><th className="text-left font-normal">Batter · bowler</th><th className="text-left font-normal">On field → final</th><th className="text-right font-normal">Result</th></tr>
          </thead>
          <tbody className="tabular-nums">
            {reviews.map(({ b, d, team }) => (
              <tr key={b.i} className="border-t border-border-default">
                <td className="py-1.5 text-text-primary">{b.label} <span className="text-text-secondary">(inns {b.innings})</span></td>
                <td className="text-text-secondary">{team}</td>
                <td className="text-text-secondary truncate">{b.striker} · {b.bowler}</td>
                <td className="text-text-primary">{DECISION[d.on_field_decision] || d.on_field_decision || "?"} → {DECISION[d.final_decision] || d.final_decision || "?"}</td>
                <td className={`text-right font-semibold ${d.overruled ? "text-emerald-400" : "text-text-secondary"}`}>{d.overruled ? "Overturned" : "Upheld"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {reviews.some((r) => r.b.wicket) && (
        <p className="mt-2 text-[10px] text-text-secondary">Wickets after review: {reviews.filter((r) => r.b.wicket).map((r) => `${r.b.dismissed || r.b.striker} (${niceWicket(r.b.wicket)})`).join(", ")}</p>
      )}
    </Card>
  );
}

export default function FieldingCharts({ balls }) {
  return (
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
      <FieldingImpact balls={balls} />
      <CatchMap balls={balls} />
      <Reviews balls={balls} />
    </div>
  );
}
